<template>
    <MainContent 
        title='Crear nueva liquidación'
    >
        <template v-slot:header>
            <div v-if="! terminado">
                <div class="pb-3">
                    <b>*Las liquidaciones solo se podrán solicitar una vez que haya pasado el evento</b> 
                </div>
            </div>
            <RecordatorioTituloComponent v-else />
        </template>
        <template v-slot:content>
            <ContentOrLoading>
                <ValidationObserver v-if="! terminado" tag="form" ref="observerLiquidacion" @submit.prevent="submitFirstStep()">
                    <div class="row" v-if="!loading">
                        <div class="col-12">
                            <div class="row">
                                <div class="col-12 col-lg-6">
                                    <div class="form-group">
                                        <label for="compania">Seleccionar compañía:</label>
                                        <ValidationProvider name="compania" rules="required" mode="passive" v-slot="{ errors }">
                                            <multiselect 
                                                v-model="data.compania" 
                                                placeholder="Seleccionar compañía" 
                                                label="nombre" 
                                                track-by="id"
                                                :options="companies" 
                                                :disabled="creando || resumen_active"
                                            >
                                                <template slot="noOptions">No hay ninguna compañía creada</template>
                                            </multiselect>
                                            <span class="error">{{ errors[0] }}</span>
                                        </ValidationProvider>
                                    </div>
                                </div>
                                <div class="col-12 col-lg-6" v-if="data.compania">
                                    <div class="form-group">
                                        <label for="fechas">Eventos disponibles:</label>
                                        <ValidationProvider name="eventos" rules="required" mode="passive" v-slot="{ errors }">
                                            <multiselect
                                                v-model="data.evento"
                                                placeholder="Seleccionar eventos" 
                                                track-by="id"
                                                label="name"
                                                :multiple="false"
                                                :options="eventos_disponibles"
                                                :disabled="creando || resumen_active"
                                            >
                                                <template slot="noOptions"><strong>No dispones de más eventos para solicitar liquidación.</strong></template>
                                            </multiselect>
                                            <span v-if="errors[0]" class="d-block invalid-feedback text-xs bg-danger white h7 mb-4 p-2 font-weight-bold"><b>{{ errors[0] }}</b></span>
                                        </ValidationProvider>
                                    </div>
                                </div>
                            </div>        
                        </div>

                        <DatosCompania
                            v-if="data.compania && data.evento"
                            :compania="data.compania"
                            :disabled="creando || resumen !== null"
                        >
                            <b-button block @click="volver" :disabled="creando || resumen !== null">
                                <i class="fa fa-arrow-up" style="margin-right: .65rem"></i>Volver<i class="fa fa-arrow-up" style="margin-left: .65rem"></i>
                            </b-button>
                        </DatosCompania>

                        <ResumenProvisional
                            :resumen="resumen"
                            :fields="fields"
                            :creando="creando"
                            :total="total"
                            @crearLiquidacion="crearLiquidacion"
                        />
                    </div>
                    <LoaderComponent v-else />
                </ValidationObserver>
                <RecordatorioComponent
                    v-else
                    @continuar="continuar"
                />
            </ContentOrLoading>
        </template>
    </MainContent>
</template>

<script>
    import MainContent from '@/components/backend/components/MainContent';
    import ContentOrLoading from '@/components/backend/components/ContentOrLoading';
    import RecordatorioComponent from '@/components/backend/liquidaciones/common/RecordatorioComponent.vue';
    import RecordatorioTituloComponent from '@/components/backend/liquidaciones/common/RecordatorioTituloComponent.vue';
    import ResumenProvisional from '@/components/backend/liquidaciones/common/ResumenProvisional.vue';
    import DatosCompania from '@/components/backend/liquidaciones/common/DatosCompania';

    export default {
        data() {
            return {
                data: {},
                eventos_disponibles: [],
                loading: false,
                resumen: null,
                terminado: false,
                creando: false,
                fields: [
                    { key: 'descripcion', label: 'Descripción' },
                    { key: 'total', label: 'Total', class: 'text-center' },
                    { key: 'importe', label: 'Importe Total', class: 'text-center' }
                ],
                reload: 1,
                resumen_active: false,
                companies: []
            }
        },
        computed: {
            teatro() {
                return this.$store.getters.getTeatro;
            },
            manager() {
                return this.$store.getters.getFooter;
            },
            total() {
                let total = 0;

                if (this.resumen) {
                    this.resumen.forEach(linea => {
                        total += parseFloat(linea.importe);
                    });
                }

                return total;
            }
        },
        methods: {
            getEventosDisponibles() {
                this.loading = true;

                const params = {
                    filters : {
                        teatro_company_id: this.data.compania?.id
                    }
                };

                this.$store.dispatch(this.config.getVuexAlias('back.liquidaciones.productos.eventos'), params).then(res => {
                    this.eventos_disponibles = res.data.data;
                }).finally(() => {
                    this.loading = false;
                });
            },
            getResumenLiquidacion() {
                this.data.filters = {};
                this.$set(this.data, 'eventos', [this.data.evento]);
            
                this.$store.dispatch(this.config.getVuexAlias('back.liquidaciones.productos.resumen'), this.data).then(res => {
                    this.resumen = res.data.data;
                    this.resumen_active = true;
                    this.scrollTo('#resumen');
                }); 
            },
            getCompanies() {
                const params = {
                    filters : {
                        to_select: true
                    },
                    teatro_id: this.teatro.id
                };

                this.$store.dispatch(this.config.getVuexAlias('back.teatro.compania-eventos.index'), params).then(res => {
                    this.companies = res.data.data;
                })
            },
            crearLiquidacion() {
                this.creando = true;
                this.$set(this.data, 'eventos', [this.data.evento]);
                this.$set(this.data, 'direccion_fiscal_id', this.data.direccionFiscal?.id);

                this.$store.dispatch(this.config.getVuexAlias('back.liquidaciones.productos.crear'), this.data).then(res => {
                    this.$toast.success(res.data.message);
                    this.terminado = true;
                    this.creando = false;
                });
            },
            async submitFirstStep() {
                const isValid = await this.$refs.observerLiquidacion.validate();
                if(isValid) {
                    this.getResumenLiquidacion();
                } else {
                    this.scrollTo(".is-invalid");
                }
            },
            continuar() {
                this.$router.push({ name: 'back.liquidaciones.productos.index' });
            },
            volver() {
                this.resumen = null;
                this.resumen_active = false;
            },
            scrollTo(paso) {
                this.$nextTick(() => {
                    this.$scrollTo(paso, 100);
                });
            }
        },
        watch: {
            'data.compania'() {
                if (this.data.compania) {
                    this.getEventosDisponibles();
                }
            }
        }, 
        created() {
            this.getCompanies();
        },
        components: {
            MainContent,
            ContentOrLoading,
            RecordatorioComponent,
            RecordatorioTituloComponent,
            ResumenProvisional,
            DatosCompania
        }
    }
</script>
